import React from "react";

import Select from "react-select";
import { useTranslation } from "react-i18next";
import { Controller } from "react-hook-form";

import { reactSelectCustomStyles } from "../../../Employees/Employee/Information";

export const CHAT_LANGUAGES: ChatLanguage[] = ["nl", "en", "de"];

/** What the server sends when a client never chose — the form starts from it. */
export const DEFAULT_CHAT_LANGUAGES: ChatLanguage[] = ["nl", "en"];

const SLOTS = ["first", "second", "third"] as const;

type Option = { value: ChatLanguage; label: string };

/**
 * The slots in order up to the first empty one, each language once. Emptying the
 * 2nd slot therefore empties the 3rd too, so the order never has a gap.
 */
const compact = (slots: (ChatLanguage | "")[]): ChatLanguage[] => {
  const firstEmpty = slots.indexOf("");

  return (firstEmpty === -1 ? slots : slots.slice(0, firstEmpty)).filter(
    (slot, index, filled): slot is ChatLanguage =>
      !!slot && filled.indexOf(slot) === index,
  );
};

const ChatLanguagesSetting = () => {
  const { t } = useTranslation(["chatLanguages"]);

  const toOption = (language: ChatLanguage): Option => ({
    value: language,
    label: t(`chatLanguages:language.${language}`),
  });

  return (
    <div className="mb-2">
      <h5>{t("chatLanguages:title")}</h5>
      <p className="text-primary mb-3">{t("chatLanguages:subtitle")}</p>
      <Controller
        name="preferences.chat.languages"
        render={({ field: { value, onChange } }) => {
          const languages: ChatLanguage[] = value?.length
            ? value
            : DEFAULT_CHAT_LANGUAGES;

          const onSlotChange = (index: number, language: ChatLanguage | "") => {
            const slots: (ChatLanguage | "")[] = SLOTS.map(
              (_slot, i) => languages[i] || "",
            );

            slots[index] = language;

            onChange(compact(slots));
          };

          return (
            <div className="d-flex flex-wrap">
              {SLOTS.map((slot, index) => {
                const taken = languages.slice(0, index);
                const options = CHAT_LANGUAGES.filter(
                  (language) => !taken.includes(language),
                ).map(toOption);

                return (
                  <div key={slot} className="d-flex mb-2 mr-3">
                    <label
                      htmlFor={`chatLanguage-${slot}`}
                      className="mb-0 mr-2 align-self-center"
                    >
                      {t(`chatLanguages:${slot}`)}
                    </label>
                    <div style={{ width: "12rem" }}>
                      <Select<Option>
                        inputId={`chatLanguage-${slot}`}
                        options={options}
                        value={
                          languages[index] ? toOption(languages[index]) : null
                        }
                        onChange={(option) =>
                          onSlotChange(index, option?.value ?? "")
                        }
                        // The 1st language is required; the 2nd and 3rd may stay empty.
                        isClearable={index > 0}
                        isDisabled={index > languages.length}
                        placeholder={t("chatLanguages:none")}
                        styles={reactSelectCustomStyles(false)}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          );
        }}
      />
    </div>
  );
};

export default ChatLanguagesSetting;
